import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Descriptions, Table, Button, Modal, Form, InputNumber, Select, DatePicker, Input, Popconfirm, notification, Typography, Space, Tag, Spin, Card } from 'antd';
import { CheckOutlined, SendOutlined, StopOutlined, EditOutlined, CreditCardOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { PdfButton, EmailButton } from '../../components/DocumentActions';
import CreditNotesCard from '../../components/CreditNotesCard';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import InvoiceEditorModal from './billing/InvoiceEditorModal';
import { INVOICE_STATUS_COLORS } from './InvoicesPage';

import { date as fmtDate } from '../../lib/format';
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PAYMENT_METHODS = ['BANK_TRANSFER', 'CARD', 'CASH', 'OTHER'];

/** One invoice: document view + the DRAFT -> APPROVED -> ISSUED -> PAID lifecycle and its receipts. */
export default function InvoiceDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const isStaff = auth.user.role !== 'CLIENT_ADMIN';
  const [invoice, setInvoice] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [lookups, setLookups] = React.useState({ clients: [], services: [], vatCodes: [], engagements: [] });
  const [paymentOpen, setPaymentOpen] = React.useState(false);
  const [paymentForm] = Form.useForm();

  const fetchInvoice = () => {
    setLoading(true);
    request
      .get(`/invoices/${id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setInvoice(r.data);
        else notification.error({ message: r.message });
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchInvoice, [id]);

  const openEditor = () => {
    const q = { company_id: invoice.company_id };
    Promise.all([
      request.get('/clients', q).then((r) => r.json()),
      request.get('/services', q).then((r) => r.json()),
      request.get('/vat-setup/vat-codes', q).then((r) => r.json()),
      request.get('/client-service-engagements', q).then((r) => r.json()),
    ]).then(([cli, srv, vat, eng]) => {
      setLookups({
        clients: cli.status ? cli.data : [],
        services: srv.status ? srv.data : [],
        vatCodes: vat.status ? vat.data : [],
        engagements: eng.status ? eng.data.filter((e) => e.client_id) : [],
      });
      setEditorOpen(true);
    });
  };

  const act = (path, successMessage) => {
    setBusy(true);
    request
      .post(`/invoices/${id}/${path}`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: successMessage });
          setInvoice(r.data);
        } else {
          notification.error({ message: r.message });
        }
      })
      .finally(() => setBusy(false));
  };

  const handleRecordPayment = () => {
    paymentForm.validateFields().then((values) => {
      setBusy(true);
      request
        .post('/payments', { ...values, invoice_id: invoice.id, payment_date: values.payment_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: `Receipt ${r.data.receipt_number} recorded` });
            setPaymentOpen(false);
            fetchInvoice();
          } else {
            notification.error({ message: r.message });
          }
        })
        .finally(() => setBusy(false));
    });
  };

  const handleVoidPayment = (payment) => {
    request
      .delete(`/payments/${payment.id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Receipt ${payment.receipt_number} voided` });
          fetchInvoice();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  if (loading || !invoice) return <Spin style={{ marginTop: 40 }} />;

  const status = invoice.status;
  const canPay = isManager && (status === 'ISSUED' || status === 'PARTIALLY_PAID');

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {invoice.invoice_number} <Tag color={INVOICE_STATUS_COLORS[status]}>{status.replace('_', ' ')}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">
            <Link to="/invoices">Invoices</Link> / {invoice.Company?.name}
          </Typography.Text>
        </div>
        {isStaff && (
          <Space wrap>
            <PdfButton type="invoice" id={+id} />
            {['ISSUED', 'PARTIALLY_PAID', 'PAID'].includes(status) && <EmailButton type="invoice" id={+id} onSent={fetchInvoice} />}
            {status === 'DRAFT' && (
              <Button icon={<EditOutlined />} onClick={openEditor}>
                Edit Draft
              </Button>
            )}
            {status === 'DRAFT' && isManager && (
              <Button type="primary" icon={<CheckOutlined />} loading={busy} onClick={() => act('approve', 'Invoice approved')}>
                Approve
              </Button>
            )}
            {status === 'APPROVED' && isManager && (
              <Button type="primary" icon={<SendOutlined />} loading={busy} onClick={() => act('issue', 'Invoice issued to client')}>
                Issue to Client
              </Button>
            )}
            {canPay && (
              <Button
                type="primary"
                icon={<CreditCardOutlined />}
                onClick={() => {
                  paymentForm.resetFields();
                  paymentForm.setFieldsValue({ payment_date: moment(), amount: Number(invoice.balance_due), method: 'BANK_TRANSFER' });
                  setPaymentOpen(true);
                }}
              >
                Record Payment
              </Button>
            )}
            {['DRAFT', 'APPROVED', 'ISSUED'].includes(status) && isManager && (
              <Popconfirm title="Cancel this invoice? Any billed time is released for re-invoicing." onConfirm={() => act('cancel', 'Invoice cancelled')}>
                <Button danger icon={<StopOutlined />} loading={busy}>
                  Cancel
                </Button>
              </Popconfirm>
            )}
          </Space>
        )}
      </Space>

      <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Client">{invoice.Client ? <Link to={`/clients/${invoice.Client.id}`}>{invoice.Client.client_name}</Link> : '-'}</Descriptions.Item>
        <Descriptions.Item label="Invoice Date">{invoice.invoice_date}</Descriptions.Item>
        <Descriptions.Item label="Due Date">{invoice.due_date}</Descriptions.Item>
        <Descriptions.Item label="Service">{invoice.Engagement?.Service?.service_name || '-'}</Descriptions.Item>
        <Descriptions.Item label="Billing Period">{invoice.period_label ? `${invoice.period_label} (${invoice.period_start} to ${invoice.period_end})` : '-'}</Descriptions.Item>
        <Descriptions.Item label="Currency">{invoice.currency}</Descriptions.Item>
        <Descriptions.Item label="Approved">{invoice.ApprovedBy ? `${invoice.ApprovedBy.name}, ${moment(invoice.approved_at).format('YYYY-MM-DD')}` : '-'}</Descriptions.Item>
        <Descriptions.Item label="Issued">{invoice.issued_at ? moment(invoice.issued_at).format('YYYY-MM-DD') : '-'}</Descriptions.Item>
        <Descriptions.Item label="Created by">{invoice.CreatedBy?.name || '-'}</Descriptions.Item>
        {isStaff && (
          <Descriptions.Item label="General ledger" span={3}>
            {(invoice.JournalEntries || []).length === 0 ? (
              <Typography.Text type="secondary">No journal entry (posted automatically when the invoice is issued, if the company's ledger accounts are set)</Typography.Text>
            ) : (
              <Space wrap>
                {invoice.JournalEntries.map((j) => (
                  <Tag key={j.id} color={j.status === 'REVERSED' ? 'orange' : 'blue'}>
                    {j.entry_number} {j.source} {fmtDate(j.entry_date)} {money(j.total_debit)} {j.status === 'REVERSED' ? '(reversed)' : ''}
                  </Tag>
                ))}
              </Space>
            )}
          </Descriptions.Item>
        )}
        {isStaff && (
          <Descriptions.Item label="VAT ledger" span={3}>
            {(invoice.VatEntries || []).length === 0 ? (
              <Typography.Text type="secondary">{status === 'ISSUED' || status === 'PARTIALLY_PAID' || status === 'PAID' ? 'Nothing posted' : 'Output VAT is posted to the VAT ledger when the invoice is issued'}</Typography.Text>
            ) : (
              <Space wrap>
                {invoice.VatEntries.map((e) => (
                  <Tag key={e.id} color={e.document_type === 'SALES_INVOICE_REVERSAL' ? 'orange' : e.needs_review ? 'red' : 'green'}>
                    {e.TransactionCode?.code || 'VAT'} {fmtDate(e.transaction_date)}: net {money(e.net_amount)} / VAT {money(e.vat_amount)} @ {Number(e.vat_rate).toFixed(0)}%
                    {e.needs_review ? ' - no VAT code, review' : ''}
                  </Tag>
                ))}
              </Space>
            )}
          </Descriptions.Item>
        )}
        {invoice.payment_instructions && (
          <Descriptions.Item label="Payment Instructions" span={3}>
            {invoice.payment_instructions}
          </Descriptions.Item>
        )}
        {invoice.notes && (
          <Descriptions.Item label="Notes" span={3}>
            {invoice.notes}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Card title="Lines" size="small" style={{ marginBottom: 16 }}>
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={invoice.InvoiceLines}
          columns={[
            { title: '#', dataIndex: 'sort_order', width: 50 },
            { title: 'Description', dataIndex: 'description' },
            { title: 'Service', key: 'service', render: (_, l) => l.Service?.service_name || '-' },
            { title: 'Qty', dataIndex: 'quantity', align: 'right', render: (v) => Number(v) },
            { title: 'Unit Price', dataIndex: 'unit_price', align: 'right', render: money },
            { title: 'Net', dataIndex: 'net_amount', align: 'right', render: money },
            { title: 'VAT', key: 'vat', align: 'right', render: (_, l) => `${money(l.vat_amount)} (${Number(l.vat_rate)}%)` },
            { title: 'Total', dataIndex: 'line_total', align: 'right', render: money },
          ]}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">{money(invoice.subtotal)}</Table.Summary.Cell>
                <Table.Summary.Cell align="right">{money(invoice.vat_amount)}</Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{money(invoice.total)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={7} align="right">
                  Paid {money(invoice.amount_paid)} - Balance due
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <strong>{money(invoice.balance_due)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      <Card title={`Payments / Receipts (${(invoice.Payments || []).length})`} size="small">
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={invoice.Payments}
          columns={[
            { title: 'Receipt', dataIndex: 'receipt_number' },
            { title: 'Date', dataIndex: 'payment_date', render: (v) => fmtDate(v) },
            { title: 'Amount', dataIndex: 'amount', align: 'right', render: money },
            { title: 'Method', dataIndex: 'method', render: (v) => v.replace('_', ' ') },
            { title: 'Reference', dataIndex: 'reference' },
            { title: 'Recorded by', key: 'by', render: (_, p) => p.CreatedBy?.name || '-' },
            ...(isManager
              ? [
                  {
                    title: '',
                    key: 'actions',
                    render: (_, p) => (
                      <Popconfirm title={`Void receipt ${p.receipt_number}? The balance is restored.`} onConfirm={() => handleVoidPayment(p)}>
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Void
                        </Button>
                      </Popconfirm>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      {isStaff && <CreditNotesCard invoice={invoice} onChanged={fetchInvoice} />}

      <Card title="Files" size="small" style={{ marginTop: 16 }}>
        <AttachmentsPanel ownerType="INVOICE" ownerId={+id} categories={['GENERAL', 'CORRESPONDENCE', 'SUPPORTING_DOCUMENT']} canDelete={isStaff} />
      </Card>

      <InvoiceEditorModal
        open={editorOpen}
        onCancel={() => setEditorOpen(false)}
        onSaved={(saved) => {
          setEditorOpen(false);
          setInvoice(saved);
        }}
        companyId={invoice.company_id}
        clients={lookups.clients}
        services={lookups.services}
        vatCodes={lookups.vatCodes}
        engagements={lookups.engagements}
        initial={invoice}
      />

      <Modal title={`Record Payment against ${invoice.invoice_number}`} open={paymentOpen} onCancel={() => setPaymentOpen(false)} onOk={handleRecordPayment} confirmLoading={busy}>
        <Form form={paymentForm} layout="vertical">
          <Form.Item name="payment_date" label="Payment Date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="amount" label={`Amount (balance due ${money(invoice.balance_due)})`} rules={[{ required: true }]}>
            <InputNumber min={0.01} max={Number(invoice.balance_due)} step={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="method" label="Method" rules={[{ required: true }]}>
            <Select options={PAYMENT_METHODS.map((m) => ({ value: m, label: m.replace('_', ' ') }))} />
          </Form.Item>
          <Form.Item name="reference" label="Reference (bank ref / auth code)">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
